/**
 * App router — all routes for One Group to Rule Them All.
 */

import { BrowserRouter, Route, Routes, Navigate } from "react-router-dom";
import { Suspense, lazy } from "react";
import { Layout } from "@/components/Layout";
import { WelcomeScreen } from "@/components/WelcomeScreen";
import { useNostrUser } from "@/hooks/useNostr";
import { Loader2 } from "lucide-react";
import type { ReactNode } from "react";

// Lazy load pages
const HomePage = lazy(() => import("@/pages/Home").then((m) => ({ default: m.HomePage })));
const GamePage = lazy(() => import("@/pages/Game").then((m) => ({ default: m.GamePage })));
const LeaderboardPage = lazy(() => import("@/pages/LeaderboardPage").then((m) => ({ default: m.LeaderboardPage })));

function PageFallback() {
  return (
    <div className="flex min-h-[60vh] items-center justify-center">
      <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
    </div>
  );
}

/** Redirect logged-in users away from welcome, logged-out users to welcome */
function HomeRedirect() {
  const { isLoggedIn } = useNostrUser();

  if (!isLoggedIn) {
    return <Navigate to="/welcome" replace />;
  }

  return (
    <Suspense fallback={<PageFallback />}>
      <HomePage />
    </Suspense>
  );
}

/** Require auth to access route */
function RequireAuth({ children }: { children: ReactNode }) {
  const { isLoggedIn } = useNostrUser();
  if (!isLoggedIn) return <Navigate to="/welcome" replace />;
  return <>{children}</>;
}

export function AppRouter() {
  return (
    <BrowserRouter>
      <Suspense fallback={<PageFallback />}>
        <Routes>
          {/* Public welcome */}
          <Route path="/welcome" element={<WelcomeScreen />} />

          {/* App routes with layout */}
          <Route element={<Layout />}>
            <Route path="/" element={<HomeRedirect />} />
            <Route
              path="/leaderboard"
              element={
                <RequireAuth>
                  <LeaderboardPage />
                </RequireAuth>
              }
            />
            <Route
              path="/game/:gameId"
              element={
                <RequireAuth>
                  <GamePage />
                </RequireAuth>
              }
            />
          </Route>

          {/* Catch-all */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
    </BrowserRouter>
  );
}

export default AppRouter;